import React from 'react';
import { useBooking } from '../context/BookingContext';

const BookingSidebar = ({ onContinue }) => {
  const { state, formatCurrency, formatDisplayDate, calculateNights } = useBooking();

  const nights = calculateNights();
  const totalQtyInCart = state.cart.reduce((acc, item) => acc + item.quantity, 0);
  const requiredRooms = state.rooms.length;

  const isCheckoutEnabled = totalQtyInCart === requiredRooms && totalQtyInCart > 0;

  const [expandedSections, setExpandedSections] = React.useState({
    bookingDetails: true,
    selectedRooms: true,
    priceBreakdown: true,
    taxDetails: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Calculate tax percentage for display
  const calculateTaxPercentage = () => {
    if (!state.subtotal || state.subtotal === 0) return '0%';
    
    const taxPercentage = (state.taxes / state.subtotal * 100).toFixed(1);
    return `${taxPercentage}%`;
  };

const calculateDisplayTotals = () => {
  const nights = calculateNights();
  
  if (state.taxBreakdown && state.taxBreakdown.perRoomBreakdowns) {
    // Calculate cumulative totals from all rooms
    let totalBaseAmount = 0;
    let totalCGST = 0;
    let totalSGST = 0;
    let totalCess = 0;
    let grandTotal = 0;
    let totalPerNightRate = 0;
    
    // Calculate per night rate from cart (for display purposes)
    totalPerNightRate = state.cart.reduce((sum, item) => 
      sum + (item.roomPrice * item.quantity), 0
    );
    
    // Calculate tax totals from all room breakdowns
    state.taxBreakdown.perRoomBreakdowns.forEach(room => {
      if (room.taxBreakdown) {
        totalBaseAmount += room.taxBreakdown.base_amount_total || 0; // This is already for the entire stay
        totalCGST += room.taxBreakdown.total_cgst_amount_after_tax || 0;
        totalSGST += room.taxBreakdown.total_sgst_amount_after_tax || 0;
        totalCess += room.taxBreakdown.total_cess_amount || 0;
        grandTotal += room.taxBreakdown.grand_total || 0;
      }
    });
    
    const totalTaxes = totalCGST + totalSGST + totalCess;
    
    return {
      roomRate: totalBaseAmount, // This is already total for stay (e.g., 3300 + 3300 = 6600)
      perNightRate: totalPerNightRate, // This is per night total (e.g., 1100 + 1100 = 2200)
      subtotal: totalBaseAmount, // Same as roomRate since no discount
      taxes: totalTaxes,
      total: grandTotal,
      finalTotal: grandTotal,
      cgst: totalCGST,
      sgst: totalSGST,
      cess: totalCess,
      nights: nights
    };
  }
  
  // Fallback
  return {
    roomRate: state.rateTotal || 0,
    perNightRate: state.cart.reduce((sum, item) => sum + (item.roomPrice * item.quantity), 0),
    subtotal: state.subtotal || 0,
    taxes: state.taxes || 0,
    total: state.total || 0,
    finalTotal: state.finalTotal || 0,
    nights: calculateNights()
  };
};

const displayTotals = calculateDisplayTotals();


const getCumulativeDisplay = () => {
  const nights = calculateNights();
  let displayText = '';
  
  if (state.taxBreakdown && state.taxBreakdown.perRoomBreakdowns) {
    const totalRooms = state.taxBreakdown.perRoomBreakdowns.length;
    const roomRateTotal = displayTotals.roomRate;
    const taxTotal = displayTotals.taxes;
    const grandTotal = displayTotals.finalTotal;
    
    displayText = `Room Rate (${nights} nights) ${formatCurrency(roomRateTotal)}`;
    
    if (totalRooms > 1) {
      // Show breakdown per room
      state.taxBreakdown.perRoomBreakdowns.forEach((room, index) => {
        displayText += `\nRoom ${index + 1}: ${formatCurrency(room.taxBreakdown.base_amount_total)} + ${formatCurrency((room.taxBreakdown.total_cgst_amount_after_tax || 0) + (room.taxBreakdown.total_sgst_amount_after_tax || 0))} tax`;
      });
    }
    
    displayText += `\nSubtotal: ${formatCurrency(displayTotals.subtotal)}`;
    displayText += `\nTaxes & Fees: ${formatCurrency(taxTotal)}`;
    displayText += `\nTotal: ${formatCurrency(grandTotal)}`;
  }
  
  return displayText;
};

  // Get tax breakdown components
const renderTaxBreakdown = () => {
  if (!state.taxBreakdown || !displayTotals.cgst) {
    const taxPercentage = displayTotals.subtotal > 0 
      ? ((displayTotals.taxes / displayTotals.subtotal) * 100).toFixed(1)
      : '0';
    
    return (
      <div className="flex justify-between text-sm">
        <span className="text-text-secondary">Taxes & Fees ({taxPercentage}%)</span>
        <span className="font-medium text-text-primary">
          {formatCurrency(displayTotals.taxes)}
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex justify-between text-sm">
        <span className="text-text-secondary">Base Amount</span>
        <span className="font-medium text-text-primary">
          {formatCurrency(displayTotals.subtotal)}
        </span>
      </div>
      
      <div className="flex justify-between text-sm pl-3">
        <span className="text-text-secondary">CGST (3.00%)</span>
        <span className="font-medium text-text-primary">
          {formatCurrency(displayTotals.cgst)}
        </span>
      </div>
      
      <div className="flex justify-between text-sm pl-3">
        <span className="text-text-secondary">SGST (3.00%)</span>
        <span className="font-medium text-text-primary">
          {formatCurrency(displayTotals.sgst)}
        </span>
      </div>
      
      {displayTotals.cess > 0 && (
        <div className="flex justify-between text-sm pl-3">
          <span className="text-text-secondary">Cess</span>
          <span className="font-medium text-text-primary">
            {formatCurrency(displayTotals.cess)}
          </span>
        </div>
      )}
      
      <div className="flex justify-between text-sm pt-2 border-t border-gray-100">
        <span className="text-text-secondary font-semibold">
          Total Taxes & Fees ({state.taxBreakdown.totalTaxPercentage || '0.00'}%)
        </span>
        <span className="font-bold text-text-primary">
          {formatCurrency(displayTotals.taxes)}
        </span>
      </div>
    </div>
  );
};

// Update the price breakdown section in the JSX
<div className="flex justify-between text-sm mb-2 lg:mb-1">
  <span className="text-text-secondary">Room Rate ({nights} night{nights !== 1 ? 's' : ''})</span>
  <span className="font-medium text-text-primary">
    {formatCurrency(displayTotals.roomRate)}
  </span>
</div>

{/* Show how many rooms are included */}
{state.cart.length > 0 && (
  <div className="text-xs text-gray-500 mb-2">
    Includes {state.cart.reduce((acc, item) => acc + item.quantity, 0)} room(s)
  </div>
)}

  // Group cart items by room number for better display
  const getRoomDisplayInfo = () => {
  const roomInfo = [];
  const nights = calculateNights();
  
  state.cart.forEach((item) => {
    for (let i = 0; i < item.quantity; i++) {
      // Find this room's tax breakdown if available
      let roomTotalForStay = 0;
      let roomTaxForStay = 0;
      
      if (state.taxBreakdown && state.taxBreakdown.perRoomBreakdowns) {
        const roomBreakdown = state.taxBreakdown.perRoomBreakdowns.find(
          rb => rb.roomName === item.roomName
        );
        if (roomBreakdown && roomBreakdown.totalTax) {
          roomTotalForStay = roomBreakdown.totalTax.grand_total || 0;
          roomTaxForStay = (roomBreakdown.totalTax.total_cgst_amount_after_tax || 0) + 
                          (roomBreakdown.totalTax.total_sgst_amount_after_tax || 0) + 
                          (roomBreakdown.totalTax.total_cess_amount || 0);
        }
      }
      
      roomInfo.push({
        roomNumber: item.roomNumber || roomInfo.length + 1,
        roomName: item.roomName,
        rateName: item.rateName,
        roomPrice: item.roomPrice, // Per night price
        roomTotalForStay: roomTotalForStay || (item.roomPrice * nights), // Total for stay
        roomTaxForStay: roomTaxForStay,
        offerName: item.offerName,
        adultsCount: item.adultsCount || item.guestInfo?.adults || 1,
        childrenCount: item.childrenCount || item.guestInfo?.children || 0,
        guestInfo: item.guestInfo
      });
    }
  });
  
  return roomInfo;
};

  const roomDisplayInfo = getRoomDisplayInfo();

  return (
    <div className="lg:sticky top-24">
      <div className="bg-white shadow-lg lg:shadow-md rounded-xl lg:rounded-lg p-5 lg:p-4 border border-gray-100">
        <p className="text-xs text-text-secondary uppercase font-semibold tracking-wide">Total Amount</p>
        <p className="text-3xl lg:text-2xl font-bold text-text-primary my-1">
            {formatCurrency(displayTotals.finalTotal)}
        </p>
        <p className="text-xs text-text-secondary mb-5 lg:mb-4">For {nights} night{nights !== 1 ? 's' : ''}</p>

        <div className="space-y-4 lg:space-y-3">
          {/* Booking Details */}
          <div className="pb-4 lg:pb-3 border-b border-gray-200">
            <button
              onClick={() => toggleSection('bookingDetails')}
              className="flex items-center justify-between w-full text-left lg:cursor-default"
            >
              <h4 className="text-xs font-semibold text-text-secondary uppercase tracking-wider">Booking Details</h4>
              <svg
                className={`lg:hidden w-4 h-4 text-text-secondary transition-transform duration-200 ${
                  expandedSections.bookingDetails ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div
              className={`
                lg:block overflow-hidden transition-all duration-300 ease-in-out
                ${expandedSections.bookingDetails ? 'max-h-96 opacity-100 mt-3' : 'max-h-0 opacity-0 lg:max-h-none lg:opacity-100 lg:mt-2'}
              `}
            >
              <div className="flex justify-between text-sm mb-2 lg:mb-1">
                <span className="text-text-secondary">Check-in</span>
                <span className="font-medium text-text-primary">
                  {formatDisplayDate(state.searchState.checkin)}
                </span>
              </div>
              <div className="flex justify-between text-sm mb-2 lg:mb-1">
                <span className="text-text-secondary">Check-out</span>
                <span className="font-medium text-text-primary">
                  {formatDisplayDate(state.searchState.checkout)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-text-secondary">Guests</span>
                <span className="font-medium text-text-primary">{state.searchState.guestCount}</span>
              </div>
            </div>
          </div>

          {/* Selected Rooms */}
          <div className="pb-4 lg:pb-3 border-b border-gray-200">
            <button
              onClick={() => toggleSection('selectedRooms')}
              className="flex items-center justify-between w-full text-left lg:cursor-default"
            >
              <h4 className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                Selected Rooms {state.cart.length > 0 && `(${totalQtyInCart})`}
              </h4>
              <svg
                className={`lg:hidden w-4 h-4 text-text-secondary transition-transform duration-200 ${
                  expandedSections.selectedRooms ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div
              className={`
                lg:block overflow-hidden transition-all duration-300 ease-in-out
                ${expandedSections.selectedRooms ? 'max-h-[600px] opacity-100 mt-3' : 'max-h-0 opacity-0 lg:max-h-none lg:opacity-100 lg:mt-2'}
              `}
            >
              <div className="space-y-3 max-h-60 lg:max-h-40 overflow-y-auto pr-2">
                {state.cart.length === 0 ? (
                  <p className="text-xs text-text-secondary">Select one or more rate plans to continue.</p>
                ) : totalQtyInCart < requiredRooms ? (
                  <p className="text-xs text-blue-700 font-semibold bg-blue-50 p-2 rounded-lg">
                    Please select {requiredRooms - totalQtyInCart} more room{requiredRooms - totalQtyInCart > 1 ? 's' : ''}.
                  </p>
                ) : totalQtyInCart > requiredRooms ? (
                  <p className="text-xs text-red-700 font-semibold bg-red-50 p-2 rounded-lg">
                    Please remove {totalQtyInCart - requiredRooms} room{totalQtyInCart - requiredRooms > 1 ? 's' : ''}.
                  </p>
                ) : null}
                
                {roomDisplayInfo.map((room, index) => (
                  <div key={index} className="pb-3 border-b border-gray-100 last:border-b-0 last:pb-0 animate-[fadeIn_0.3s_ease-in]">
                    <div className="flex justify-between items-start mb-1">
                      <div className="flex-1">
                        <div className="flex justify-between items-center gap-2 mb-1">
                          <span className="font-bold text-text-primary text-sm">
                            Room {room.roomNumber}: {room.roomName}
                          </span>
                          <span className="text-text-primary font-bold flex-shrink-0 text-sm">
                            {formatCurrency(room.roomPrice)}
                          </span>
                        </div>
                        
                        <p className="text-xs text-text-secondary mb-1">
                          {nights} night{nights !== 1 ? 's' : ''}: {formatCurrency(room.roomPrice * nights)}
                          {room.roomTaxForStay > 0 && (
                            <span className="ml-2 text-gray-500">
                              (incl. {formatCurrency(room.roomTaxForStay)} tax)
                            </span>
                          )}
                        </p>
                        
                        <p className="text-xs text-text-secondary mb-1">
                          {room.adultsCount} Adult{room.adultsCount !== 1 ? 's' : ''}
                          {room.childrenCount > 0 && 
                            `, ${room.childrenCount} Child${room.childrenCount !== 1 ? 'ren' : ''}`
                          }
                        </p>
                        
                        <p className="text-xs text-text-secondary font-medium">
                          Plan: {room.rateName}
                        </p>
                      </div>
                    </div>
                    
                    {room.offerName && (
                      <p className="text-xs font-semibold text-green-700 mt-1 bg-green-50 px-2 py-1 rounded">
                        🎁 {room.offerName} Applied
                      </p>
                    )}
                  </div>
                ))}

              </div>
            </div>
          </div>

          {/* Price Breakdown - UPDATED VERSION */}
          <div className="space-y-4 lg:space-y-3">
            {/* Price Breakdown Section */}
            <div>
              <button
                onClick={() => toggleSection('priceBreakdown')}
                className="flex items-center justify-between w-full text-left mb-3 lg:mb-2 lg:cursor-default"
              >
                <h4 className="text-xs font-semibold text-text-secondary uppercase tracking-wider">Price Breakdown</h4>
                <svg
                  className={`lg:hidden w-4 h-4 text-text-secondary transition-transform duration-200 ${
                    expandedSections.priceBreakdown ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div
                className={`
                  lg:block overflow-hidden transition-all duration-300 ease-in-out
                  ${expandedSections.priceBreakdown ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 lg:max-h-none lg:opacity-100'}
                `}
              >
                {/* Room Rate with Per Night Breakdown */}
                <div className="flex justify-between text-sm mb-2 lg:mb-1">
                  <div>
                    <span className="text-text-secondary">Room Rate</span>
                   
                    {state.taxBreakdown && state.taxBreakdown.perRoomBreakdowns && (
                      <div className="text-xs text-gray-500 mt-1">
                        {state.taxBreakdown.perRoomBreakdowns.map((room, idx) => (
                          <div key={idx} className="ml-2">
                            Room {idx + 1}: {formatCurrency(room.perNightPrice)} × {displayTotals.nights} = {formatCurrency(room.totalForStay)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <span className="font-medium text-text-primary text-right">
                    {formatCurrency(displayTotals.roomRate)}
                    
                  </span>
                </div>
                
                {/* Subtotal */}
                <div className="flex justify-between text-sm mb-2 lg:mb-1">
                  <span className="text-text-secondary font-semibold">Subtotal</span>
                  <span className="font-semibold text-text-primary">
                    {formatCurrency(displayTotals.subtotal)}
                  </span>
                </div>
                
                {/* Dynamic Tax Breakdown */}
                {renderTaxBreakdown()}
                
                {/* Total Amount */}
                <div className="flex justify-between text-base font-bold pt-3 mt-3 border-t border-gray-200">
                  <span>Total Amount</span>
                  <span>{formatCurrency(displayTotals.finalTotal)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Continue Button */}
          <div className="pt-3 lg:pt-2">
            <button
              onClick={onContinue}
              disabled={!isCheckoutEnabled}
              className={`w-full bg-brand-accent text-text-dark-on-accent px-6 py-3.5 lg:py-2 rounded-lg lg:rounded-md font-semibold text-base hover:bg-brand-accent-hover transition-all duration-200 active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-accent shadow-lg lg:shadow-none ${
                !isCheckoutEnabled ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              Continue to Checkout
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingSidebar;